import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import TooltipHandler from '@/components/ui/custom/TooltipHandler';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ImageInput from '@/components/ui/custom/dataInput/ImageInput';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import SelectInput from '@/components/ui/custom/dataInput/SelectInput';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import { addCourse, updateCourse } from '@/data/services/courseServices';
import {
  courseLinesQueryKeys,
  courseModulesQueryKeys,
} from '@/data/services/querykeys';
import { courseGenerator } from '@/functions/generators';
import {
  Course,
  CourseDurationEnum,
  CourseDurationMinuteEnum,
  CourseFormatEnum,
} from '@/models/Course';
import { CourseModule } from '@/models/CourseModule';
import { ApiError } from '@/models/Errors';
import alert from '@/utils/shared/alert';
import { validateUpload } from '@/utils/VerifyImageFile';
import { buildChangedObject } from '@/utils/buildChangedObject';
import { adapterModuleCourse } from '@/utils/coursePath';
import { ArrowDownOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Dispatch, SetStateAction, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface CourseFormProps {
  course: Partial<Course>;
  updateCourseList(): void;
  isEditMode: boolean;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
}

type CourseFormData = Omit<Course, 'module'> & {
  module?: CourseModule | null;
};

export default function CourseForm(props: CourseFormProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageCourse.editCourse',
  });
  const { t: tFormats } = useTranslation('translation', {
    keyPrefix: 'courseFormats',
  });

  const { course, updateCourseList, isEditMode, setIsEditMode } = props;

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [hasChanges, setHasChanges] = useState(false);

  const [bannerImg, setBannerImg] = useState<File | string | null>(
    course.bannerImg ?? null,
  );

  const isNew = !course.id;

  const status = [
    {
      text: t('active'),
      value: true,
    },
    {
      text: t('inactive'),
      value: false,
    },
  ];

  const {
    register,
    reset,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<CourseFormData>({
    defaultValues: course,
  });

  const [courseLine, setCourseLine] = useState(course.module?.courseLine);

  const onSubmitEditCourse: SubmitHandler<CourseFormData> = async data => {
    const { id } = data;
    try {
      setIsLoading(true);
      const courseAdapter = adapterModuleCourse(data as Partial<Course>);

      if (!courseAdapter) return;

      const changedFields = buildChangedObject(course, courseAdapter);

      const changedBanner =
        typeof bannerImg !== 'string' ? bannerImg : undefined;

      if (Object.keys(changedFields).length || changedBanner) {
        await updateCourse(id, {
          ...changedFields,
          bannerImg: changedBanner,
        });
        feedbackForm();
        alert.success(t('saveEdited'));
      }
    } catch (error: any) {
      const apiError = new ApiError(error);
      alert.error(apiError.getErrorMessage());
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitAddCourse: SubmitHandler<CourseFormData> = async data => {
    try {
      setIsLoading(true);

      const courseToCreate = adapterModuleCourse(data as Course);
      if (!courseToCreate) return;
      await addCourse(courseToCreate, bannerImg);

      reset(
        courseGenerator({
          id: 0,
          name: '',
          abbreviation: '',
          bannerImg: null,
        }),
      );
      setBannerImg(null);
      feedbackForm();
      alert.success(t('saveCreated'));
    } catch (error: any) {
      const apiError = new ApiError(error);
      alertError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const alertError = (apiError: ApiError) => {
    if (apiError.fields.includes('abbreviation')) {
      const fieldMessage = apiError.getMessageByField('abbreviation');

      if (fieldMessage) {
        alert.warning(fieldMessage);
      }
    } else {
      alert.error(apiError.getErrorMessage());
    }
  };

  const feedbackForm = () => {
    updateCourseList();
    setHasChanges(false);
    setIsEditMode(false);
  };

  const onCancel = () => {
    reset(course);
    setBannerImg(course.bannerImg ?? null);
    setHasChanges(false);
    setIsEditMode(false);
  };

  const onImageChange: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (e.target.files && e.target.files.length) {
      const value = e.target.files.item(0);
      if (value && !validateUpload(value)) {
        setBannerImg(value);
        setHasChanges(true);
      }
    }
  };

  const onImageRemove = () => {
    setBannerImg(null);
    setHasChanges(true);
  };

  const onSubmit = isNew ? onSubmitAddCourse : onSubmitEditCourse;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col w-full gap-4"
    >
      <TextInput
        color={errors.name?.message ? 'warning' : 'primary'}
        dataTestId="nameInput"
        label={t('name')}
        register={register('name', {
          maxLength: {
            message: t('errors.name.maxLength'),
            value: 200,
          },
          onChange: () => {
            setHasChanges(true);
          },
          required: t('wanings.emptyField'),
        })}
        disabled={!isEditMode}
        labelPosition="left"
        errorLabelText={errors.name?.message}
        className={{ base: 'font-500 text-16' }}
      />

      <TextInput
        color={errors.abbreviation?.message ? 'warning' : 'primary'}
        dataTestId="abbreviationInput"
        label={t('abbreviation')}
        register={register('abbreviation', {
          maxLength: {
            message: t('errors.abbreviation.maxLength'),
            value: 7,
          },

          onChange: () => {
            setHasChanges(true);
          },
          required: t('wanings.emptyField'),
        })}
        disabled={!isEditMode}
        labelPosition="left"
        errorLabelText={errors.abbreviation?.message}
        className={{ base: 'font-500 text-16' }}
      />

      <InfiniteSearchInput
        selectedItem={courseLine}
        service={courseLinesQueryKeys.list}
        displayName={line => line.name}
        onSelect={line => {
          setCourseLine(line);
          setValue('module', null);
          setHasChanges(true);
        }}
        onDeselect={() => {
          setCourseLine(undefined);
          setValue('module', null);
          setHasChanges(true);
        }}
        input={{
          testId: 'courseLineInput',
          label: t('courseLine'),
          labelPosition: 'left',
          disabled: !isEditMode,
          className: { base: 'font-500 text-16' },
        }}
      />

      <Controller
        control={control}
        name="module"
        rules={{ required: t('wanings.emptyField') }}
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <InfiniteSearchInput
              selectedItem={value ?? null}
              service={filters =>
                courseModulesQueryKeys.list(courseLine?.id ?? 0, filters)
              }
              options={{ enabled: !!courseLine?.id }}
              displayName={module => module.name}
              onSelect={module => {
                onChange(module);
                setHasChanges(true);
              }}
              onDeselect={() => {
                onChange(undefined);
                setHasChanges(true);
              }}
              input={{
                testId: 'courseModuleInput',
                label: t('courseModule'),
                labelPosition: 'left',
                errorLabelText: error?.message,
                disabled: !isEditMode || !courseLine?.id,
                className: { base: 'font-500 text-16' },
              }}
            />
          );
        }}
      />

      <SelectInput
        testId="durationInput"
        label={t('duration')}
        labelPosition="left"
        register={register('duration', {
          onChange: () => {
            setHasChanges(true);
          },
          required: t('wanings.emptyField'),
        })}
        disabled={!isEditMode}
        errorLabelText={errors.duration?.message}
        className={{ base: 'font-500 text-16' }}
      >
        {(
          Object.keys(CourseDurationEnum) as (keyof typeof CourseDurationEnum)[]
        ).map(state => {
          return (
            <option key={state} value={CourseDurationMinuteEnum[state]}>
              {CourseDurationEnum[state]}
            </option>
          );
        })}
      </SelectInput>
      <Controller
        control={control}
        name="formats"
        rules={{ required: t('wanings.emptyField') }}
        render={({ field: { onChange, value } }) => (
          <SelectInput
            testId="formatInput"
            label={t('format')}
            labelPosition="left"
            value={value?.[0] ?? ''}
            onChange={e => {
              onChange([e.target.value as CourseFormatEnum]);
              setHasChanges(true);
            }}
            disabled={!isEditMode}
            className={{ base: 'font-500 text-16' }}
          >
            {Object.keys(CourseFormatEnum).map(format => {
              return (
                <option key={format} value={format}>
                  {tFormats(format)}
                </option>
              );
            })}
          </SelectInput>
        )}
      />

      <ConditionalRenderer condition={!isNew}>
        <SelectInput
          testId="isActiveInput"
          label={t('isActive')}
          labelPosition="left"
          register={register('isActive', {
            onChange: () => {
              setHasChanges(true);
            },
            required: t('wanings.emptyField'),
          })}
          disabled={!isEditMode}
          className={{ base: 'font-500 text-16' }}
          errorLabelText={errors.isActive?.message}
        >
          {status.map(({ text, value }) => {
            return (
              <option key={text} value={value.toString()}>
                {text}
              </option>
            );
          })}
        </SelectInput>
      </ConditionalRenderer>

      <ImageInput
        testId="bannerImgInput"
        className="w-fit"
        isDisabled={!isEditMode}
        image={bannerImg}
        onChange={onImageChange}
        onRemove={onImageRemove}
      />

      <ConditionalRenderer condition={isEditMode}>
        <span className="flex justify-end gap-5">
          <MainButton
            dataTestId="cancelButton"
            type="button"
            onClick={onCancel}
            icon={<XMarkIcon />}
            text={t('cancelButton')}
            color="neutral"
          />

          <TooltipHandler
            renderTooltip={!hasChanges}
            tooltipMessage={t('wanings.noChanges')}
          >
            <MainButton
              dataTestId="saveButton"
              type="submit"
              icon={<ArrowDownOnSquareIcon />}
              text={t('saveButton')}
              loading={isLoading}
              disabled={!hasChanges}
            />
          </TooltipHandler>
        </span>
      </ConditionalRenderer>
    </form>
  );
}
